/*
 * The typed API client. Every call goes to `${apiBase}/api/v1<path>`; in mock mode MSW answers in the
 * browser (src/api/mock). Errors are RFC 9457 problem details, thrown as ApiError.
 *
 *   const servers = await api.get<Page<Server>>('/servers', { query: { search: 'rimal' }, signal })
 *   const created = await api.post<Server>('/servers', { name, hostname })
 *
 * Hooks in src/api/hooks/* wrap these calls with TanStack Query; components never call fetch directly.
 */
import { apiBase } from '@/lib/env'
import { i18n } from '@/i18n'
import type { Problem } from './types/common'
import { authToken, sessionEvents } from './token'

export class ApiError extends Error {
  readonly status: number
  readonly problem: Problem

  constructor(problem: Problem) {
    super(problem.detail ?? problem.title)
    this.name = 'ApiError'
    this.status = problem.status
    this.problem = problem
  }

  get code(): string {
    return this.problem.code
  }
  get correlationId(): string {
    return this.problem.correlationId
  }
  /** field → first message, for forms (see applyFieldErrors in src/lib/forms.ts) */
  get fieldErrors(): Record<string, string> {
    const out: Record<string, string> = {}
    for (const [k, v] of Object.entries(this.problem.errors ?? {})) if (v[0]) out[k] = v[0]
    return out
  }
}

export function isApiError(e: unknown): e is ApiError {
  return e instanceof ApiError
}

type QueryValue = string | number | boolean | null | undefined
export type RequestOptions = {
  query?: Record<string, QueryValue>
  signal?: AbortSignal
  headers?: Record<string, string>
  /** skip the session-expired event for this call (sign-in, the session probe) */
  anonymous?: boolean
}

function newCorrelationId(): string {
  return typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `cid_${Date.now().toString(36)}${Math.random().toString(36).slice(2, 10)}`
}

function buildUrl(path: string, query?: Record<string, QueryValue>): string {
  const url = `${apiBase}/api/v1${path.startsWith('/') ? path : `/${path}`}`
  if (!query) return url
  const params = new URLSearchParams()
  for (const [k, v] of Object.entries(query)) {
    if (v === undefined || v === null || v === '') continue
    params.set(k, String(v))
  }
  const qs = params.toString()
  return qs ? `${url}?${qs}` : url
}

async function request<T>(
  method: string,
  path: string,
  body: unknown,
  opts: RequestOptions & { as?: 'json' | 'blob' } = {},
): Promise<T> {
  const correlationId = newCorrelationId()
  const token = authToken.get()
  const headers: Record<string, string> = {
    Accept: 'application/json, application/problem+json',
    'Accept-Language': i18n.language || 'ar',
    'X-Correlation-Id': correlationId,
    ...opts.headers,
  }
  if (token) headers.Authorization = `Bearer ${token}`
  if (body !== undefined) headers['Content-Type'] = 'application/json'

  let res: Response
  try {
    res = await fetch(buildUrl(path, opts.query), {
      method,
      headers,
      body: body === undefined ? undefined : JSON.stringify(body),
      signal: opts.signal,
    })
  } catch (e) {
    if (e instanceof DOMException && e.name === 'AbortError') throw e
    throw new ApiError({
      type: 'about:blank',
      title: 'Network error',
      status: 0,
      code: 'network_error',
      correlationId,
    })
  }

  if (res.status === 204) return undefined as T
  if (opts.as === 'blob' && res.ok) return (await res.blob()) as T
  const text = await res.text()
  const data: unknown = text ? safeJson(text) : undefined

  if (!res.ok) {
    const p = (data && typeof data === 'object' ? data : {}) as Partial<Problem>
    const problem: Problem = {
      // keep the extension members (availableAt, requiredRoles, …) the API merges into the body
      ...p,
      type: p.type ?? 'about:blank',
      title: p.title ?? res.statusText,
      status: p.status ?? res.status,
      detail: p.detail,
      instance: p.instance,
      code: p.code ?? `http_${res.status}`,
      correlationId: p.correlationId ?? res.headers.get('X-Correlation-Id') ?? correlationId,
      errors: p.errors,
    }
    if (res.status === 401 && token && !opts.anonymous) sessionEvents.emitExpired()
    throw new ApiError(problem)
  }
  return data as T
}

function safeJson(text: string): unknown {
  try {
    return JSON.parse(text)
  } catch {
    return text
  }
}

export const api = {
  get: <T>(path: string, opts?: RequestOptions) => request<T>('GET', path, undefined, opts),
  post: <T>(path: string, body?: unknown, opts?: RequestOptions) =>
    request<T>('POST', path, body ?? {}, opts),
  put: <T>(path: string, body?: unknown, opts?: RequestOptions) => request<T>('PUT', path, body ?? {}, opts),
  patch: <T>(path: string, body?: unknown, opts?: RequestOptions) =>
    request<T>('PATCH', path, body ?? {}, opts),
  delete: <T = void>(path: string, opts?: RequestOptions) => request<T>('DELETE', path, undefined, opts),
  /** A file the API answers with (e.g. an invoice PDF), fetched with the session; errors are problems as usual. */
  blob: (path: string, opts?: RequestOptions) =>
    request<Blob>('GET', path, undefined, { ...opts, headers: { Accept: '*/*', ...opts?.headers }, as: 'blob' }),
}
